/**
 * Client-side updater for #category-listing-itemlist-json-ld on SS-enabled categories.
 *
 * See: docs/superpowers/specs/2026-05-29-searchspring-itemlist-sync-design.md
 */

/**
 * Read the latest SearchSpring category.json cache entry from sessionStorage.
 *
 * Category-page cache entries are keyed `/api/search/category.json/...` and have
 * shape `{ value: { results, pagination, ... }, expires }`. NOTE: this differs
 * from the search-page shape (`/api/search/search.json/...` with an extra
 * `value.value` wrapper) that the Tealium helper handles. We can't reuse
 * `getLatestSearchFromCache` because it filters by `/api/search/search` only
 * and assumes the double-value shape.
 *
 * @returns {{results: Array, pagination: object} | null}
 */
function readCategoryResultsFromCache() {
    const raw = sessionStorage.getItem('ss-networkcache');
    if (raw == null) return null;
    const allEntries = JSON.parse(raw);
    const categoryEntries = Object.entries(allEntries)
        .filter(([key]) => key.startsWith('/api/search/category'))
        .map(([, v]) => v)
        .filter(v => Array.isArray(v?.value?.results));
    if (categoryEntries.length === 0) return null;
    const latest = categoryEntries.reduce(
        (best, cur) => (!best || cur.expires > best.expires ? cur : best),
        null,
    );
    return {
        results: latest?.value?.results,
        pagination: latest?.value?.pagination,
    };
}

/**
 * Build a summary-format ItemList JSON-LD payload from SearchSpring cache results.
 *
 * Pure function — no DOM, no globals.
 *
 * @param {Array<{url: string}>} results — SS cache results array (filtered to valid URLs)
 * @param {object} pagination — SS cache pagination object. Accepted but ignored in v1
 *                              (page-relative numbering); reserved for a future global-numbering migration.
 * @returns {object} ItemList JSON-LD payload
 */
export function buildItemListPayload(results, pagination) {
    const itemListElement = results.map((r, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: r.url,
    }));
    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: itemListElement.length,
        itemListElement,
    };
}

/**
 * Sync `#category-listing-itemlist-json-ld` to match SearchSpring's current grid state.
 *
 * Side-effecting. Called from listingPagesLoaded() on every SS state change.
 *
 * No-op invariant: every early-return branch (gates 0-4 below) must not mutate any DOM.
 * The script tag is created in <head> if missing — SearchSpring wipes the
 * partial-rendered script when it replaces #searchspring-content's inner HTML, so
 * we can't rely on the server-rendered tag being present post-mount. Creation
 * happens only AFTER gates 0-4 pass (SS enabled + cache has usable data), so SS-off
 * or no-data states never inject anything.
 */
export function syncCategoryItemListFromSearchSpring() {
    // Gate 0: category pages only. readCategoryResultsFromCache() grabs the
    // LATEST category.json cache entry with no current-page check, so running
    // on brand/search pages would inject a stale category ItemList into that
    // page's <head> (those pages never server-render the tag — only
    // templates/components/f/category/product-listing.html does).
    if (globalThis.BC_CONTEXT?.pageType !== 'category') {
        return;
    }

    // Gate 1: SS toggle
    if (String(globalThis.BC_CONTEXT?.enableSearchSpring).toLowerCase() !== 'true') {
        return;
    }

    // Gate 2: read SS sessionStorage for category.json cache entries
    let cache;
    try {
        cache = readCategoryResultsFromCache();
    } catch (err) {
        console.debug('[listing-itemlist-updater] cache read threw, leaving block alone:', err);
        return;
    }
    if (cache == null) return;

    // Gate 3: extract results array
    const { results, pagination } = cache;
    if (!Array.isArray(results) || results.length === 0) {
        return;
    }

    // Gate 4: filter to records with valid urls
    const validResults = results.filter(
        r => typeof r?.url === 'string' && r.url.length > 0,
    );
    if (validResults.length === 0) {
        console.warn(
            '[listing-itemlist-updater] cache had results but none had a valid result.url',
        );
        return;
    }

    // Locate or create the script tag. SS may have wiped the server-rendered one.
    let script = document.getElementById('category-listing-itemlist-json-ld');
    if (script == null) {
        script = document.createElement('script');
        script.id = 'category-listing-itemlist-json-ld';
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
    }

    // Gate 5: signature dedupe (only meaningful when the script already had a sig)
    const sig = validResults.map(r => r.url).join('|');
    if (script.getAttribute('data-listing-sig') === sig) {
        return;
    }

    // Step 6: write payload, then signature (order matters)
    const payload = buildItemListPayload(validResults, pagination);
    script.textContent = JSON.stringify(payload);
    script.setAttribute('data-listing-sig', sig);
}
